import type React from "react";

import "@fullcalendar/react/dist/vdom";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import Select from "react-select";
import type {
  CalendarOptions,
  EventClickArg,
  DateSelectArg,
} from "@fullcalendar/common";
import { useState, useCallback, useEffect, useRef } from "react";
import Button from "src/base-components/Button";
import Lucide from "src/base-components/Lucide";
import moment from "moment";
import Card from "../Card";
import { FormInput } from "src/base-components/Form";
import DatePicker from "src/base-components/DatePicker";
import type { DateValueType } from "react-tailwindcss-datepicker";
import { remindersColorsTips } from "src/helpers";
import Tippy from "src/base-components/Tippy";
import {
  fetchReminderCalendarService,
  completeReminderService,
  snoozeReminderService,
} from "src/services/reminder.services";
import { FormTextarea, FormSelect } from "src/base-components/Form";
import Dialog from "src/base-components/Headless/Dialog";

import type { ReminderEvent, ReminderType } from "src/types/reminder";
import "./styles.css";
import ReactSelect from "src/base-components/ReactSelect";
import { fetchAllUsers } from "src/stores/user";
import { connect } from "react-redux";
import type { RootState } from "src/stores";
import { useReminderCalendarRedux } from "src/stores/reminderCalendar/hooks";
import {
  clearFilters as clearReduxFilters,
  setFilters as setReduxFilters,
  fetchReminderCalendar,
} from "src/stores/reminderCalendar/slice";
import { useDispatch } from "react-redux";
import { set } from "lodash";

// Reminder Type Constants
const TYPE_GENERAL = "general";
const TYPE_FOLLOW_UP = "follow_up";
const TYPE_PAYMENT = "payment";
const TYPE_BOOKING = "booking";
const TYPE_CALL = "call";
const TYPE_EMAIL = "email";
const TYPE_MEETING = "meeting";
const TYPE_DOCUMENTATION = "documentation";

// API Response interfaces based on the actual response structure
interface AssignedUser {
  id: number;
  name: string;
  last_name: string;
  email: string;
  pivot: {
    reminder_id: string;
    user_id: number;
    status: "pending" | "completed" | "overdue" | "snoozed";
    completed_at: string | null;
    completion_remarks: string | null;
    snoozed_until: string | null;
    snooze_count: number;
    is_notified: number;
    notified_at: string | null;
  };
}

interface Assignment {
  id: string;
  reminder_id: string;
  user_id: number;
  status: "pending" | "completed" | "overdue" | "snoozed";
  completed_at: string | null;
  completion_remarks: string | null;
  snoozed_until: string | null;
  snooze_count: number;
  is_notified: boolean;
  notified_at: string | null;
  status_text: string;
}

interface ReminderData {
  id: string;
  query_id: string | null;
  title: string;
  description: string;
  created_by: {
    id: number;
    name: string;
    last_name: string;
    email: string;
  };
  remind_on: string;
  remind_before_minutes: number;
  reminder_type: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  reminder_type_text: string;
  overall_status: "pending" | "all_completed" | "partially_completed";
  query_record: any;
  assigned_users: AssignedUser[];
  assignments: Assignment[];
}

interface APIReminderEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  description: string;
  backgroundColor: string;
  borderColor: string;
  extendedProps: {
    reminder: ReminderData;
    status: "pending" | "all_completed" | "partially_completed";
    type: string;
  };
}

interface ReminderCalendarProps {
  events?: ReminderEvent[];
  onEventClick?: (event: ReminderEvent) => void;
  onDateSelect?: (date: Date) => void;
  onAddReminder?: () => void;
  refreshTrigger?: number; // Add this to trigger refetch when reminders are created
  onViewChange?: (startDate: Date, endDate: Date) => void; // New prop for handling view changes
  onSearchChange?: (searchTerm: string) => void; // Handle search changes
  onFilterChange?: (filters: {
    reminderType?: string;
    status?: string;
    userId?: string | number | Array<string | number> | undefined; // backward-compat
    selectedAgentId?: string | number; // preferred for parent Reminders page
    sortBy?: "created_at" | "remind_on";
    sortOrder?: "asc" | "desc";
  }) => void; // Handle filter changes
  // Dedicated sort handlers (avoid misusing onFilterChange which drops other filters)
  onSortByChange?: (value: "created_at" | "remind_on") => void;
  onSortOrderToggle?: () => void;
  // agentOptions?: { label: string; val: string | number; default?: boolean }[];
  isSingle?: boolean;
  allAgentOptions: { label: string; value: number }[];
  fetchUsers?: (payload?: any) => void;
  parentFilterDate?: DateValueType;
  onClearFilter: () => void; // optional date range coming from parent
  selectedAgent: (number | string)[];
  setSelectedAgent: (agent: (number | string)[]) => void;
  selectedReminderType?: string;
  setSelectedReminderType: (type: string) => void;

  selectedReminderTypeValue?: string;
  setSelectedReminderTypeValue: (value: string) => void;
  selectedStatus?: string;
  setSelectedStatus: (status: string) => void;

  selectedStatusLabel?: string;
  setSelectedStatusLabel: (label: string) => void;
  search: string;
  setSearch: (value: string) => void;
}

function ReminderCalendar({
  allAgentOptions,
  events: propEvents,
  onEventClick,
  onDateSelect,
  onClearFilter,
  onAddReminder,
  refreshTrigger = 0,
  onViewChange,
  onSearchChange,
  onFilterChange,
  onSortByChange: onSortByChangeExternal,
  onSortOrderToggle: onSortOrderToggleExternal,
  // agentOptions = [],
  isSingle = false,
  fetchUsers,
  parentFilterDate,

  selectedAgent,
  setSelectedAgent,
  selectedReminderType,
  setSelectedReminderType,
  selectedReminderTypeValue,
  setSelectedReminderTypeValue,
  selectedStatus,
  setSelectedStatus,
  selectedStatusLabel,
  setSelectedStatusLabel,
  search,
  setSearch,
}: ReminderCalendarProps) {
  // Ref to control FullCalendar instance (e.g., jump to today)
  const calendarRef = useRef<FullCalendar | null>(null);
  // Storage key for persisting filter state across reloads/remounts.
  const FILTER_STORAGE_KEY = "reminderCalendarFilters";
  // Read any persisted filters synchronously to avoid initial render flash with placeholders
  const persisted = (() => {
    try {
      return JSON.parse(localStorage.getItem(FILTER_STORAGE_KEY) || "{}") || {};
    } catch {
      return {} as any;
    }
  })();
  const [filterDate, setFilterDate] = useState<DateValueType>({
    startDate: "",
    endDate: "",
  });

  // If the parent passes a date range, sync it to the local filterDate so
  // the DatePicker reflects selections made in the parent (e.g., List view)
  // useEffect(() => {
  //   try {
  //     const prevStart = filterDate?.startDate || "";
  //     const prevEnd = filterDate?.endDate || "";
  //     const nextStart = parentFilterDate?.startDate || "";
  //     const nextEnd = parentFilterDate?.endDate || "";
  //     // Only sync when parent provides a complete date range (both start
  //     // and end). Ignore empty or partial parent values to avoid wiping the
  //     // user's current selection during incidental re-renders.
  //     if (nextStart && nextEnd) {
  //       if (prevStart !== nextStart || prevEnd !== nextEnd) {
  //         console.log(
  //           "ReminderCalendar: syncing filterDate from parentFilterDate",
  //           parentFilterDate
  //         );
  //         setFilterDate({ startDate: nextStart, endDate: nextEnd });
  //       }
  //     }
  //   } catch (e) {}
  // }, [parentFilterDate, filterDate]);

  const [events, setEvents] = useState<ReminderEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentViewStart, setCurrentViewStart] = useState<Date | null>(null);
  const [currentViewEnd, setCurrentViewEnd] = useState<Date | null>(null);
  // Sorting state for calendar
  const [sortBy, setSortBy] = useState<"created_at" | "remind_on">("remind_on");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const sortMenuRef = useRef<HTMLDivElement | null>(null);
  // Helper: show loader while waiting for parent-provided events after a filter/sort/search/date change.
  const triggerParentLoading = () => {
    if (propEvents) {
      setLoading(true);
    }
  };

  // Ref for search debouncing
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Modal state for reminder actions
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedReminder, setSelectedReminder] =
    useState<ReminderEvent | null>(null);

  const [actionLoading, setActionLoading] = useState(false);
  const [completionRemarks, setCompletionRemarks] = useState("");
  const [snoozeMinutes, setSnoozeMinutes] = useState("30");

  // Snooze options
  const snoozeOptions = [
    { value: "15", label: "15 minutes" },
    { value: "30", label: "30 minutes" },
    { value: "60", label: "1 hour" },
    { value: "120", label: "2 hours" },
    { value: "240", label: "4 hours" },
    { value: "480", label: "8 hours" },
    { value: "1440", label: "1 day" },
    { value: "2880", label: "2 days" },
    { value: "4320", label: "3 days" },
    { value: "10080", label: "1 week" },
  ];

  useEffect(() => {
    if (fetchUsers) {
      fetchUsers({ type: 1 } as any);
    }
  }, []);

  // useEffect(() => {
  //   if (users) {
  //     console.log("Users updated in ReminderCalendar:", users);
  //   }
  // }, [users]);

  // Redux integration for reminder calendar state management
  const dispatch = useDispatch();
  const {
    events: reduxEvents,
    filters: reduxFilters,
    loading: reduxLoading,
    clearAllFilters,
    updateFilters,
    fetchWithFilters,
  } = useReminderCalendarRedux();

  // Flag to track if we should use Redux for state management
  const useReduxForClearFilters = !propEvents; // Use Redux when not in prop-driven mode

  // Get reminder type colors
  const getReminderTypeColors = (reminderType?: string) => {
    switch (reminderType) {
      case "call":
        return {
          backgroundColor: "#3b82f6", // Blue
          borderColor: "#3b82f6",
          textColor: "white",
        };
      case "follow_up":
        return {
          backgroundColor: "#10b981", // Green
          borderColor: "#10b981",
          textColor: "white",
        };
      case "email":
        return {
          backgroundColor: "#ef4444", // Red
          borderColor: "#ef4444",
          textColor: "white",
        };
      case "booking":
        return {
          backgroundColor: "#8b5cf6", // Purple
          borderColor: "#8b5cf6",
          textColor: "white",
        };
      case "payment":
        return {
          backgroundColor: "#f59e0b", // Amber
          borderColor: "#f59e0b",
          textColor: "white",
        };
      case "documentation":
        return {
          backgroundColor: "#14b8a6", // Teal
          borderColor: "#14b8a6",
          textColor: "white",
        };
      default:
        return {
          backgroundColor: "#6b7280", // Gray
          borderColor: "#6b7280",
          textColor: "white",
        };
    }
  };

  // Transform API response to calendar events
  const transformAPIResponseToEvents = (
    apiEvents: APIReminderEvent[],
  ): ReminderEvent[] => {
    return apiEvents.map((apiEvent) => {
      const reminderData = apiEvent.extendedProps.reminder;
      const extendedProps = apiEvent.extendedProps;

      // Get assigned users names
      const assignedUserNames = reminderData.assigned_users
        .map((user) => `${user.name} ${user.last_name}`)
        .join(", ");
      const assignedTo = assignedUserNames || "Unassigned";

      // Use the status from API response - prioritize overall_status and extendedProps.status
      let status: "pending" | "completed" | "overdue" | "snoozed" = "pending";

      // Map the API status values to our component status values
      if (
        reminderData.overall_status === "all_completed" ||
        extendedProps.status === "all_completed"
      ) {
        status = "completed";
      } else if (
        reminderData.overall_status === "partially_completed" ||
        extendedProps.status === "partially_completed"
      ) {
        // For partially completed, check individual assignments to determine overall status
        const hasOverdueAssignments = reminderData.assignments.some(
          (assignment) => assignment.status === "overdue",
        );
        const hasCompletedAssignments = reminderData.assignments.some(
          (assignment) => assignment.status === "completed",
        );
        const hasSnoozedAssignments = reminderData.assignments.some(
          (assignment) => assignment.status === "snoozed",
        );

        if (hasOverdueAssignments) {
          status = "overdue";
        } else if (hasSnoozedAssignments) {
          status = "snoozed";
        } else {
          status = "pending";
        }
      } else {
        // Check individual assignments for specific status
        const firstAssignment = reminderData.assignments[0];
        if (firstAssignment) {
          switch (firstAssignment.status) {
            case "completed":
              status = "completed";
              break;
            case "overdue":
              status = "overdue";
              break;
            case "snoozed":
              status = "snoozed";
              break;
            default:
              status = "pending";
          }
        }
      }

      // Determine priority based on remind_before_minutes value (closer reminder = higher priority)
      let priority: "high" | "medium" | "low" = "medium";
      if (reminderData.remind_before_minutes <= 15) {
        priority = "high";
      } else if (reminderData.remind_before_minutes >= 60) {
        priority = "low";
      }

      // Use colors from API response or determine based on status
      let backgroundColor = apiEvent.backgroundColor;
      let borderColor = apiEvent.borderColor;

      // Map common status colors if not provided by API
      if (!backgroundColor) {
        switch (status) {
          case "completed":
            backgroundColor = "#28a745"; // Green
            borderColor = "#28a745";
            break;
          case "overdue":
            backgroundColor = "#dc3545"; // Red
            borderColor = "#dc3545";
            break;
          case "snoozed":
            backgroundColor = "#fd7e14"; // Orange
            borderColor = "#fd7e14";
            break;
          default:
            backgroundColor = "#007bff"; // Blue
            borderColor = "#007bff";
        }
      }

      return {
        id: apiEvent.id,
        title: apiEvent.title,
        start: apiEvent.start,
        end: apiEvent.end,
        backgroundColor,
        borderColor,
        textColor: "white",
        description: apiEvent.description,
        assignedTo,
        priority,
        status,
        reminderType: reminderData.reminder_type as ReminderType,
        reminderBeforeMinutes: reminderData.remind_before_minutes,
        assignments: reminderData.assignments,
        assignedUsers: reminderData.assigned_users,
        reminderData,
      };
    });
  };

  // Fetch reminders from API using calendar endpoint with date range and filters
  const fetchReminders = useCallback(
    async (
      startDate?: Date,
      endDate?: Date,
      searchTerm?: string,
      filterDateRange?: DateValueType,
      userId?: string | number | (string | number)[] | undefined,
      reminderType?: string,
      status?: string,
      sortByParam?: "created_at" | "remind_on",
      sortOrderParam?: "asc" | "desc",
    ) => {
      if (propEvents) {
        // Use prop events if provided - don't fetch internally
        // Only update local events if the incoming propEvents differ from
        // current events to avoid unnecessary emptying/repopulating flicker.
        try {
          const prev = JSON.stringify(events || []);
          const next = JSON.stringify(propEvents || []);
          if (prev !== next) {
            setEvents(propEvents);
          } else {
            console.log(
              "ReminderCalendar: propEvents identical to current events, skipping update",
            );
          }
        } catch (e) {
          setEvents(propEvents);
        }
        setLoading(false);
        return;
      }

      // Use provided dates or fallback to current month for calendar view
      let start = startDate || moment().startOf("month").toDate();
      let end = endDate || moment().endOf("month").toDate();

      // If no explicit start/end were provided but a filter date range exists,
      // use the filterDateRange. This prevents persisted filter dates from
      // overriding explicit calendar navigation dates (datesSet).
      if (
        !startDate &&
        !endDate &&
        filterDate?.startDate &&
        filterDate?.endDate
      ) {
        // Make the end date inclusive by using startOf/endOf day
        start = moment(filterDate.startDate).startOf("day").toDate();
        end = moment(filterDate.endDate).endOf("day").toDate();
      }

      const startDateStr = moment(start).format("YYYY-MM-DD");
      const endDateStr = moment(end).format("YYYY-MM-DD");

      if (!loading) setLoading(true);
      setError(null);

      try {
        const response = await fetchReminderCalendarService(
          startDateStr,
          endDateStr,
          searchTerm,
          reminderType,
          status,
          userId,
          sortByParam ?? sortBy,
          sortOrderParam ?? sortOrder,
        );

        const arrayResp = Array.isArray(response) ? response : [];
        const transformedEvents = transformAPIResponseToEvents(
          arrayResp as any,
        );
        setEvents(transformedEvents);
      } catch (err: any) {
        console.error("Error fetching reminders:", err);
        setError("Failed to load reminders");
        // On error, keep existing events instead of clearing to avoid UI flicker.
        // Do not call `setEvents([])` here.
      } finally {
        setLoading(false);
      }
    },
    [propEvents, sortBy, sortOrder],
  );

  // Fetch reminders when refreshTrigger changes (after creating/updating reminders)
  useEffect(() => {
    if (refreshTrigger > 0 && !propEvents) {
      fetchReminders(
        currentViewStart || undefined,
        currentViewEnd || undefined,
        search,
        filterDate,
        selectedAgent,
        selectedReminderTypeValue ?? undefined,
        selectedStatus ?? undefined,
        sortBy,
        sortOrder,
      );
    }
  }, [refreshTrigger, currentViewStart, currentViewEnd, fetchReminders]);

  // Handle prop events and log events updates
  useEffect(() => {
    if (propEvents) {
      try {
        const prev = JSON.stringify(events || []);
        const next = JSON.stringify(propEvents || []);

        if (prev !== next) {
          setEvents(propEvents);
        } else {
          console.log(
            "ReminderCalendar: propEvents identical — skipping update",
          );
        }
      } catch (e) {
        // fallback to naive update on stringify error
        setEvents(propEvents);
      }
      setLoading(false); // Ensure loading is turned off when events are set
      setError(null); // Clear any errors when new events are received
    }
  }, [propEvents]);

  // Debug effect to monitor events state changes
  useEffect(() => {
    console.log("Events state updated:", events);
  }, [events]);

  // Cleanup search timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Close sort dropdown on outside click
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (sortMenuOpen && sortMenuRef.current) {
        if (!sortMenuRef.current.contains(e.target as Node)) {
          setSortMenuOpen(false);
        }
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [sortMenuOpen]);

  const handleEventClick = (clickInfo: EventClickArg) => {
    const reminderData = clickInfo.event.extendedProps?.reminder;
    const event: ReminderEvent = {
      id: clickInfo.event.id,
      title: clickInfo.event.title,
      start: clickInfo.event.startStr,
      end: clickInfo.event.endStr,
      backgroundColor: clickInfo.event.backgroundColor || undefined,
      borderColor: clickInfo.event.borderColor || undefined,
      textColor: clickInfo.event.textColor || undefined,
      description: clickInfo.event.extendedProps?.description,
      assignedTo: clickInfo.event.extendedProps?.assignedTo,
      priority: clickInfo.event.extendedProps?.priority,
      status: clickInfo.event.extendedProps?.status,
      reminderType: clickInfo.event.extendedProps?.reminderType,
      reminderData: reminderData,
      query: clickInfo.event.extendedProps?.query, // Include query field for navigation
      assignments: reminderData?.assignments, // Add assignments from reminderData
      assigned_users: reminderData?.assigned_users, // Add assigned users
      assignedUsers: reminderData?.assigned_users, // Also map to assignedUsers
      // Map the fields to what the modal expects
      reminderBeforeMinutes:
        reminderData?.remind_before_minutes || reminderData?.reminder_before, // Map reminder_before to reminderBeforeMinutes
      createdFor: reminderData?.created_by?.name
        ? `${reminderData.created_by.name} ${
            reminderData.created_by.last_name || ""
          }`.trim()
        : "Unknown", // Map created_by to createdFor
      scheduledTime: clickInfo.event.extendedProps?.scheduledTime, // Use already formatted scheduledTime from extendedProps
      // Keep original fields for reference
      created_by: reminderData?.created_by,
      created_at: reminderData?.created_at,
      scheduled_at: reminderData?.scheduled_at,
      reminder_before: reminderData?.reminder_before,
    };

    // Always use parent callback if provided (for consistent modal experience)
    if (onEventClick) {
      onEventClick(event);
      return;
    }
    // Fallback: Show action modal only if no parent callback is provided
    if (event.status === "pending" || event.status === "overdue") {
      setSelectedReminder(event);
      setShowActionModal(true);
      setCompletionRemarks("");
      setSnoozeMinutes("30");
    }
  };

  // Handle reminder completion
  const handleCompleteReminder = async () => {
    if (!selectedReminder || !selectedReminder.reminderData) return;

    setActionLoading(true);
    try {
      await completeReminderService({
        id: selectedReminder.reminderData.id,
        completion_remarks: completionRemarks,
        callback: (hasError: boolean, data: any) => {
          if (!hasError) {
            try {
              const detail: any = {
                id: selectedReminder.reminderData.id,
                response: data,
              };
              const result = data?.result || data?.data || data;
              if (result && typeof result === "object") {
                if (result.reminder_id) detail.reminder_id = result.reminder_id;
                if (result.reminder && result.reminder.id)
                  detail.reminder_id = result.reminder.id;
                if (result.assignment_id)
                  detail.assignment_id = result.assignment_id;
                if (result.id) detail.assignment_id = result.id;
              }
              window.dispatchEvent(
                new CustomEvent("reminder-updated", { detail }),
              );
            } catch (e) {}
            // Refresh the calendar with current filters
            fetchReminders(
              currentViewStart || undefined,
              currentViewEnd || undefined,
              search,
              filterDate,
              selectedAgent,
              selectedReminderTypeValue ?? undefined,
              selectedStatus ?? undefined,
              sortBy,
              sortOrder,
            );
            setShowActionModal(false);
            setSelectedReminder(null);
            setCompletionRemarks("");
          }
        },
      });
    } catch (error) {
      console.error("Error completing reminder:", error);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle reminder snooze
  const handleSnoozeReminder = async () => {
    if (!selectedReminder || !selectedReminder.reminderData) return;

    setActionLoading(true);
    try {
      await snoozeReminderService({
        id: selectedReminder.reminderData.id,
        snooze_minutes: Number.parseInt(snoozeMinutes),
        callback: (hasError: boolean, data: any) => {
          if (!hasError) {
            try {
              const detail: any = {
                id: selectedReminder.reminderData.id,
                response: data,
              };
              const result = data?.result || data?.data || data;
              if (result && typeof result === "object") {
                if (result.reminder_id) detail.reminder_id = result.reminder_id;
                if (result.reminder && result.reminder.id)
                  detail.reminder_id = result.reminder.id;
                if (result.assignment_id)
                  detail.assignment_id = result.assignment_id;
                if (result.id) detail.assignment_id = result.id;
              }
              window.dispatchEvent(
                new CustomEvent("reminder-updated", { detail }),
              );
            } catch (e) {}
            // Refresh the calendar with current filters
            fetchReminders(
              currentViewStart || undefined,
              currentViewEnd || undefined,
              search,
              filterDate,
              selectedAgent,
              selectedReminderTypeValue ?? undefined,
              selectedStatus ?? undefined,
              sortBy,
              sortOrder,
            );
            setShowActionModal(false);
            setSelectedReminder(null);
            setSnoozeMinutes("30");
          }
        },
      });
    } catch (error) {
      console.error("Error snoozing reminder:", error);
    } finally {
      setActionLoading(false);
    }
  };

  // Close modal
  const handleCloseModal = () => {
    setShowActionModal(false);
    setSelectedReminder(null);
    setCompletionRemarks("");
    setSnoozeMinutes("30");
  };

  const handleDateSelect = (selectInfo: DateSelectArg) => {
    // if (onDateSelect) {
    //   onDateSelect(selectInfo.start);
    // }
  };

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value;
    setSearch(searchValue);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce the search API call
    searchTimeoutRef.current = setTimeout(() => {
      if (propEvents && onSearchChange) {
        // If using prop events, call parent callback
        triggerParentLoading();
        onSearchChange(searchValue);
      } else {
        // Otherwise, fetch internally

        fetchReminders(
          currentViewStart || undefined,
          currentViewEnd || undefined,
          searchValue,
          filterDate,
          selectedAgent,
          selectedReminderTypeValue ?? undefined,
          selectedStatus ?? undefined,
          sortBy,
          sortOrder,
        );
      }
    }, 500); // 500ms debounce
  };

  const onDateChange = (value: DateValueType) => {
    setFilterDate(value);

    if (propEvents && onViewChange) {
      triggerParentLoading();
      // If using prop events, call parent onViewChange with date range
      if (value?.startDate && value?.endDate) {
        const startDate = moment(value.startDate).toDate();
        const endDate = moment(value.endDate).toDate();

        onViewChange(startDate, endDate);
      } else {
        onViewChange(
          currentViewStart || moment().startOf("month").toDate(),
          currentViewEnd || moment().endOf("month").toDate(),
        );
      }
    } else {
      fetchReminders(
        undefined,
        undefined,
        search,
        value,
        selectedAgent,
        selectedReminderTypeValue ?? undefined,
        selectedStatus ?? undefined,
      );
    }
  };

  // const onClearFilter = () => {
  //   console.log("🧹 Clearing filters using Redux Toolkit...");

  //   // Reset UI filters
  //   setSearch("");
  //   setFilterDate({ startDate: null, endDate: null });
  //   setSelectedAgent(null);
  //   setSelectedReminderType("");
  //   setSelectedStatus("");

  //   if (searchTimeoutRef.current) {
  //     clearTimeout(searchTimeoutRef.current);
  //   }

  //   if (useReduxForClearFilters) {
  //     // Use Redux to manage clear filters - this will restore initial filters on first clear
  //     console.log("🔁 Using Redux to clear filters and restore initial state");
  //     clearAllFilters(true); // Use initial filters if available

  //     // Update Redux store with cleared filter state
  //     updateFilters({
  //       search: "",
  //       userId: undefined,
  //       reminderType: undefined,
  //       status: undefined,
  //       sortBy: "created_at",
  //       sortOrder: "desc",
  //     });
  //   } else if (propEvents && onFilterChange) {
  //     // Parent-driven mode: notify parent about filter changes
  //     console.log("🔁 Using prop events to clear filters");
  //     triggerParentLoading();
  //     try {
  //       onSearchChange && onSearchChange("");
  //     } catch (e) {}
  //     try {
  //       onFilterChange && onFilterChange({ clearDates: true } as any);
  //     } catch (e) {}
  //     console.log(
  //       "📅 Requested parent to clear date filters (clearDates=true)"
  //     );
  //   } else {
  //     // Fallback to previous behavior
  //     console.log("🔁 Fallback: Loading current month...");
  //     const start = moment().startOf("month").format("YYYY-MM-DD");
  //     const end = moment().endOf("month").format("YYYY-MM-DD");
  //     console.log("start---->start Before", start, "end----->end", end);
  //     fetchReminders(
  //       moment(start).toDate(),
  //       moment(end).toDate(),
  //       "",
  //       undefined,
  //       undefined,
  //       undefined,
  //       undefined,
  //       sortBy,
  //       sortOrder
  //     );
  //     console.log("start---->start After", start, "end----->end", end);
  //     console.log(`📅 Fallback reload for current month: ${start} → ${end}`);
  //   }
  // };

  // const onAgentChange = (ids: number[]) => {
  //   // setSelectedAgent(value);
  //   // If parent is handling events, notify via onFilterChange (best-effort)
  //   if (propEvents && onFilterChange) {
  //     triggerParentLoading();
  //     try {
  //       onFilterChange({ userId: ids ?? [] } as any);
  //     } catch (e) {
  //       // swallow; parent may not expect agent filter specifically
  //     }
  //     return;
  //   }

  //   // Otherwise refetch reminders with current filters and the newly selected agent id
  //   fetchReminders(
  //     currentViewStart || undefined,
  //     currentViewEnd || undefined,
  //     search,
  //     filterDate,
  //     ids,
  //     selectedReminderTypeValue ?? undefined,
  //     selectedStatus ?? undefined
  //   );
  // };

  const onAgentChange = (ids: number[] | string[]) => {
    // If parent handles filter change
    if (onFilterChange) {
      // triggerParentLoading();
      try {
        const userIdStr = ids.join(",");
        // Preserve existing reminder type & status selections so they are not cleared
        onFilterChange({
          userId: userIdStr,
          reminderType: selectedReminderTypeValue || undefined,
          status: selectedStatus || undefined,
          search: search || undefined,
        } as any);
        // console.log("onAgentChange ids userId:", { userId: ids });
        // onFilterChange({ userId: ids });
      } catch (e) {}
    }

    // Always update child response too
    // fetchReminders(
    //   currentViewStart || undefined,
    //   currentViewEnd || undefined,
    //   search,
    //   filterDate,
    //   ids,
    //   selectedReminderTypeValue ?? undefined,
    //   selectedStatus ?? undefined
    // );
  };

  // Sort handlers for calendar list
  // const onSortByChange = (v: "created_at" | "remind_on") => {
  //   setSortBy(v);
  //   if (propEvents && onSortByChangeExternal) {
  //     // Let parent handle preserving existing filters & date range
  //     try {
  //       onSortByChangeExternal(v);
  //     } catch (e) {}
  //     return;
  //   }
  //   // Local (uncontrolled) mode fetch
  //   fetchReminders(
  //     currentViewStart || undefined,
  //     currentViewEnd || undefined,
  //     search,
  //     filterDate,
  //     selectedAgent,
  //     selectedReminderTypeValue ?? undefined,
  //     selectedStatus ?? undefined,
  //     v,
  //     sortOrder
  //   );
  // };

  // const onSortOrderToggle = () => {
  //   const nextOrder: "asc" | "desc" = sortOrder === "asc" ? "desc" : "asc";
  //   setSortOrder(nextOrder);
  //   if (propEvents && onSortOrderToggleExternal) {
  //     try {
  //       onSortOrderToggleExternal();
  //     } catch (e) {}
  //     return;
  //   }
  //   // Local fetch preserving current filters
  //   fetchReminders(
  //     currentViewStart || undefined,
  //     currentViewEnd || undefined,
  //     search,
  //     filterDate,
  //     selectedAgent,
  //     selectedReminderTypeValue ?? undefined,
  //     selectedStatus ?? undefined,
  //     sortBy,
  //     nextOrder
  //   );
  // };

  // Persist filters whenever they change (debounce search separately via searchTimeoutRef logic for performance).
  // useEffect(() => {
  //   console.log("useeffect localStorage");
  //   try {
  //     localStorage.setItem(
  //       FILTER_STORAGE_KEY,
  //       JSON.stringify({
  //         // search,
  //         filterDate,
  //         // persist both label and value for reminder type and status label
  //         selectedReminderTypeLabel: selectedReminderType,
  //         selectedReminderTypeValue: selectedReminderTypeValue,
  //         selectedStatusLabel,
  //         selectedStatus,
  //         selectedAgent,
  //       })
  //     );
  //   } catch (e) {
  //     // ignore persistence errors
  //   }
  // }, [
  //   search,
  //   filterDate,
  //   selectedReminderType,
  //   selectedReminderTypeValue,
  //   selectedStatus,
  //   selectedStatusLabel,
  //   selectedAgent,
  // ]);

  // Sync local filter changes with Redux store when not in prop-driven mode
  useEffect(() => {
    if (useReduxForClearFilters) {
      const startDate = filterDate?.startDate
        ? moment(filterDate.startDate).format("YYYY-MM-DD")
        : undefined;
      const endDate = filterDate?.endDate
        ? moment(filterDate.endDate).format("YYYY-MM-DD")
        : undefined;

      updateFilters({
        search,
        startDate,
        endDate,
        userId: selectedAgent,
        reminderType: selectedReminderTypeValue,
        status: selectedStatus,
        sortBy,
        sortOrder,
      });
    }
  }, [
    useReduxForClearFilters,
    search,
    filterDate,
    selectedAgent,
    selectedReminderTypeValue,
    selectedStatus,
    sortBy,
    sortOrder,
    updateFilters,
  ]);

  // Calculate statistics
  const today = moment().format("YYYY-MM-DD");
  const totalReminders = events.filter(
    (event) => event.status !== "completed",
  ).length;
  const dueToday = events.filter(
    (event) =>
      moment(event.start).format("YYYY-MM-DD") === today &&
      event.status !== "completed",
  ).length;
  const overdue = events.filter(
    (event) =>
      moment(event.start).isBefore(today, "day") &&
      event.status !== "completed",
  ).length;
  const completed = events.filter(
    (event) => event.status === "completed",
  ).length;

  // Events are already filtered by the API, so no need for local filtering
  const filteredEvents = events;

  const DatePickerCallback = useCallback(
    () => (
      <DatePicker
        value={filterDate}
        onChange={(value) => {
          onDateChange(value);
        }}
        classNames={{
          input: () =>
            "max-h-[38px] transition duration-200 ease-in-out w-full text-sm border-slate-300/100 shadow-sm !rounded-md placeholder:text-slate-400/90 focus:ring-primary focus:ring-opacity-20 focus:border-none focus:ring-4",
        }}
        placeholder="Select date range"
        separator="-"
        inputId="date-picker"
        showFooter={true}
      />
    ),
    [
      filterDate,
      onDateChange, // ← IMPORTANT
      selectedAgent,
      selectedReminderTypeValue,
      selectedStatus,
      search,
    ],
  );

  const options: CalendarOptions = {
    plugins: [dayGridPlugin, interactionPlugin],
    initialView: "dayGridMonth",
    // Hide previous/next month date cells' events; focus only current month
    showNonCurrentDates: false,
    headerToolbar: {
      left: "prev,next",
      center: "title",
      right: "",
    },
    height: "auto",
    events: filteredEvents.map((event) => ({
      ...event,
      extendedProps: {
        description: event.description,
        assignedTo: event.assignedTo,
        priority: event.priority,
        status: event.status,
        reminderType: event.reminderType,
        reminderData: event.reminderData,
        query: event.query, // Include query field for navigation
      },
    })),
    eventClick: handleEventClick,
    selectable: true,
    select: handleDateSelect,
    dayMaxEvents: 4,
    moreLinkClick: "popover",
    eventDisplay: "block",
    displayEventTime: false,
    eventClassNames: ["reminder-event"],
    dayCellClassNames: (arg) => {
      const today = moment().format("YYYY-MM-DD");
      const cellDate = moment(arg.date).format("YYYY-MM-DD");
      return cellDate === today ? ["today-cell"] : [];
    },
    datesSet: (dateInfo) => {
      // Called when the calendar view changes (navigation, initial load)

      const newStart = dateInfo.start;
      const newEnd = dateInfo.end;

      // Keep the raw calendar view bounds for internal state/UI. Note:
      // FullCalendar's `dateInfo.end` is exclusive (first millisecond after
      // the last visible day). We compute an inclusive API range below.
      setCurrentViewStart(newStart);
      setCurrentViewEnd(newEnd);

      if (onViewChange) {
        onViewChange(newStart, newEnd);
      } else if (!propEvents) {
        // Restrict fetch strictly to the visible month (1st -> last day)
        // Use newStart as anchor for the current month being displayed.
        const monthStart = moment(newStart)
          .startOf("month")
          .startOf("day")
          .toDate();
        const monthEnd = moment(newStart).endOf("month").endOf("day").toDate();

        fetchReminders(
          monthStart,
          monthEnd,
          search,
          undefined,
          selectedAgent,
          selectedReminderTypeValue ?? undefined,
          selectedStatus ?? undefined,
          sortBy,
          sortOrder,
        );
      }
    },
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e?.target?.name;
    const value = e?.target?.value;
    // setSelectedAgent([...selectedAgent, [name]: value ]);
  };

  return (
    <div className="reminder-calendar-container">
      {/* Loading State */}
      {loading && (
        <Card className="p-6 mb-4">
          <div className="flex items-center justify-center">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
              <span className="text-slate-600">Loading reminders... </span>
            </div>
          </div>
        </Card>
      )}

      {/* Error State */}
      {error && (
        <Card className="p-6 mb-4 border-red-200 bg-red-50">
          <div className="flex items-center space-x-2">
            <Lucide icon="AlertCircle" className="w-4 h-4 text-red-600" />
            <span className="text-red-600">{error}</span>
            <Button
              onClick={() => {
                fetchReminders(
                  currentViewStart || undefined,
                  currentViewEnd || undefined,
                  search,
                  filterDate,
                  selectedAgent,
                  selectedReminderTypeValue ?? undefined,
                  selectedStatus ?? undefined,
                  sortBy,
                  sortOrder,
                );
              }}
              size="sm"
              variant="outline-secondary"
              className="ml-auto"
            >
              <Lucide icon="RotateCcw" className="w-4 h-4 mr-1" />
              Retry
            </Button>
          </div>
        </Card>
      )}

      {/* Statistics Toolbar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <Card className="p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 font-medium mb-1">
                Total Reminders
              </p>
              <p className="text-2xl font-bold text-slate-800">
                {totalReminders}
              </p>
            </div>
            <div className="p-2 bg-blue-100 rounded-lg">
              <Lucide icon="Calendar" className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </Card>

        <Card className="p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 font-medium mb-1">
                Due Today
              </p>
              <p className="text-2xl font-bold text-slate-800">{dueToday}</p>
            </div>
            <div className="p-2 bg-orange-100 rounded-lg">
              <Lucide icon="Clock" className="w-5 h-5 text-orange-600" />
            </div>
          </div>
        </Card>

        <Card className="p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-600 font-medium mb-1">Overdue</p>
              <p className="text-2xl font-bold text-red-600">{overdue}</p>
            </div>
            <div className="p-2 bg-red-100 rounded-lg">
              <Lucide icon="AlertTriangle" className="w-5 h-5 text-red-600" />
            </div>
          </div>
        </Card>

        <Card className="p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 font-medium mb-1">
                Completed
              </p>
              <p className="text-2xl font-bold text-green-600">{completed}</p>
            </div>
            <div className="p-2 bg-green-100 rounded-lg">
              <Lucide icon="CheckCircle" className="w-5 h-5 text-green-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Search and Filter Toolbar */}
      <Card className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-y-2 mb-8 p-5 ">
        <div className="flex flex-col sm:flex-row gap-2  items-start z-20">
          <div className="flex items-center  border border-slate-300 rounded-lg shadow-sm sm:w-56 bg-white px-1.5 max-h-[40px]  ">
            <Lucide
              icon="Search"
              // className="absolute inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500"
            />
            <FormInput
              type="text"
              value={search}
              placeholder="Search..."
              className="  border-none shadow-none focus:ring-0 focus:border-none focus:placeholder:text-slate-400 "
              onChange={onSearch}
            />
          </div>

          <div className="relative">
            <DatePickerCallback />
          </div>

          {/* Reminder Type Filter */}
          <div className="relative">
            <ReactSelect
              options={[
                { label: "All Types", value: "" },
                { label: "Follow Up", value: TYPE_FOLLOW_UP },
                { label: "Meeting", value: TYPE_MEETING },
                { label: "Call", value: TYPE_CALL },
                { label: "Email", value: TYPE_EMAIL },
              ]}
              placeholder={selectedReminderType || "All Types"}
              value={(() => {
                // Map by stored value (kept separately) to ensure the option
                // object is found correctly even though we store the label
                // in `selectedReminderType` for display.
                if (!selectedReminderTypeValue) return null;
                return (
                  [
                    { label: "All Types", value: "" },
                    { label: "Follow Up", value: TYPE_FOLLOW_UP },
                    { label: "Meeting", value: TYPE_MEETING },
                    { label: "Call", value: TYPE_CALL },
                    { label: "Email", value: TYPE_EMAIL },
                  ].find((o) => o.value === selectedReminderTypeValue) || null
                );
              })()}
              containerClassName={
                isSingle ? "min-w-[8rem] ml-2" : "min-w-[8rem] "
              }
              onChange={(selected) => {
                const val = (selected as any) || null;
                // store label for display, value for API
                setSelectedReminderType(val?.label || undefined);
                setSelectedReminderTypeValue(val?.value || undefined);
                // If using propEvents, notify parent filters
                if (propEvents && onFilterChange) {
                  try {
                    onFilterChange({ reminderType: val?.value || undefined });
                  } catch (e) {}
                } else {
                  // Fetch with new reminder type filter
                  fetchReminders(
                    currentViewStart || undefined,
                    currentViewEnd || undefined,
                    search,
                    filterDate,
                    selectedAgent,
                    val?.value,
                    selectedStatus ?? undefined,
                    sortBy,
                    sortOrder,
                  );
                }
              }}
              className="min-w-36"
            />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <ReactSelect
              options={[
                { label: "All Status", value: "" },
                { label: "Completed", value: "all_completed" },
                { label: "Snoozed", value: "snoozed" },
                { label: "Overdue", value: "overdue" },
                { label: "Pending", value: "pending" },
              ]}
              placeholder={selectedStatusLabel || "All Status"}
              value={(() => {
                if (!selectedStatus) return null;
                return (
                  [
                    { label: "All Status", value: "" },
                    { label: "Completed", value: "all_completed" },
                    { label: "Snoozed", value: "snoozed" },
                    { label: "Overdue", value: "overdue" },
                    { label: "Pending", value: "pending" },
                  ].find((o) => o.value === selectedStatus) || null
                );
              })()}
              containerClassName={
                isSingle ? "min-w-[7rem] ml-2" : "min-w-[7rem]"
              }
              onChange={(selected) => {
                const val = (selected as any) || null;
                // store both value and label for status (value used for API)
                setSelectedStatus(val?.value || undefined);
                setSelectedStatusLabel(val?.label || undefined);
                if (propEvents && onFilterChange) {
                  try {
                    onFilterChange({ status: val?.value || undefined });
                  } catch (e) {}
                } else {
                  fetchReminders(
                    currentViewStart || undefined,
                    currentViewEnd || undefined,
                    search,
                    filterDate,
                    selectedAgent,
                    selectedReminderTypeValue ?? undefined,
                    val?.value || undefined,
                    sortBy,
                    sortOrder,
                  );
                }
              }}
              className="min-w-36"
            />
          </div>

          {/* <ReactSelect
            options={[
              { label: "All Users", value: "" },
              ...(allAgentOptions?.map((u) => ({
                label: `${(u?.name || u?.first_name || "").toString()} ${(
                  u?.last_name ||
                  u?.lastName ||
                  ""
                ).toString()}`.trim(),
                value: u?.id,
                raw: u,
              })) || []),
            ]}
            placeholder={"All Users"}
            value={(() => {
              if (!selectedAgent || selectedAgent?.value === "") return null;
              // Ensure we map to option object when options haven't yet loaded.
              const found = (allAgentOptions || []).find(
                (u: any) => `${u?.id}` === `${selectedAgent?.value}`
              );
              if (found) {
                return {
                  label: `${(
                    found?.name ||
                    found?.first_name ||
                    ""
                  ).toString()} ${(
                    found?.last_name ||
                    found?.lastName ||
                    ""
                  ).toString()}`.trim(),
                  value: found?.id,
                } as any;
              }
              return selectedAgent; // fallback; component can handle raw object
            })()}
            containerClassName={
              isSingle ? "min-w-[7rem] ml-auto" : "min-w-[7rem] ml-3"
            }
            onChange={onAgentChange}
            className="min-w-36"
          /> */}

          {/* <ReactSelect
            isMulti
            options={allAgentOptions} // NO EMPTY OPTION
            placeholder="Select Users"
            value={allAgentOptions.filter((opt) =>
              selectedAgent?.includes(opt.value)
            )}
            onChange={(selected: any[]) => {
    const ids = (selected || []).map(s => Number(s.value)); // ensure numbers
    setSelectedAgent(ids);
  }}
            className="min-w-36"
          /> */}

          {/* <ReactSelect
            isMulti
            options={allAgentOptions}
            value={allAgentOptions.filter((opt) =>
              selectedAgent?.includes(opt.value)
            )}
            onChange={(selected) => {
              console.log("Selected users:--->", selected);
              // setSelectedAgent((selected || [])?.map((s) => Number(s.value)));
            }}
            placeholder="Select Users"
          /> */}
          {/* <Select
            isMulti
            options={allAgentOptions}
            value={allAgentOptions.filter(
              (opt) =>
                Array.isArray(selectedAgent) &&
                selectedAgent.includes(Number(opt.value))
            )}
            onChange={(selected: any) => {
              const options = Array.isArray(selected) ? selected : [];
              const ids = options.map((o: any) => Number(o.value));
              setSelectedAgent(ids);
              onAgentChange(ids);
            }}
            placeholder="Select Users"
            className="min-w-36"
          /> */}

          <Select
            isMulti
            options={allAgentOptions}
            value={allAgentOptions.filter(
              (opt) =>
                Array.isArray(selectedAgent) &&
                selectedAgent.includes(opt.value.toString()), // no need Number()
            )}
            onChange={(selected) => {
              const selectedArr = Array.isArray(selected) ? selected : [];

              // Extract values as (number | string)[]
              const ids = selectedArr.map((o) => o.value.toString());
              setSelectedAgent(ids); // local state
              onAgentChange(ids); // pass to parent
            }}
            styles={{
              control: (provided, state) => ({
                ...provided,

                boxShadow: state.isFocused ? "0 0 0 1px #cbd5e1" : "none",

                "&:focus-within": { borderColor: "inherit" },
                borderRadius: "0.375rem",
              }),
            }}
            placeholder="All Users"
            className="min-w-36"
          />

          {/* <div className="ml-4 flex items-center gap-3">
   
            <div>
              <ReactSelect
                options={[
                  { label: "Created At", value: "created_at" },
                  { label: "Remind On", value: "remind_on" },
                ]}
                placeholder={"Sort By"}
                value={(() => {
                  return (
                    [
                      { label: "Created At", value: "created_at" },
                      { label: "Remind On", value: "remind_on" },
                    ].find((o) => o.value === sortBy) || null
                  );
                })()}
                onChange={(selected) => {
                  const v = (selected as any)?.value as
                    | "created_at"
                    | "remind_on"
                    | undefined;
                  if (v && v !== sortBy) onSortByChange(v);
                }}
                className="min-w-36"
                containerClassName={
                  isSingle ? "min-w-[9rem] ml-0" : "min-w-[9rem] ml-0"
                }
              />
            </div>

            <button
              className="p-1.5 rounded-md hover:bg-slate-100 border border-slate-300/60"
              onClick={() => {
                onSortOrderToggle();
              }}
              title={sortOrder === "asc" ? "Ascending" : "Descending"}
            >
              {sortOrder === "asc" ? (
                <div className="flex items-center text-sky-800">
                  <Lucide icon="ArrowUp" className="w-4 h-4 mr-1" />
                  <div className="flex flex-col leading-none text-[10px] font-bold">
                    <span>Z</span>
                    <span>A</span>
                  </div>
                </div>
              ) : (
                <div className="flex items-center text-sky-800">
                  <Lucide icon="ArrowDown" className="w-4 h-4 mr-1" />
                  <div className="flex flex-col leading-none text-[10px] font-bold">
                    <span>A</span>
                    <span>Z</span>
                  </div>
                </div>
              )}
            </button>
          </div> */}
          <div className="flex flex-row justify-center items-center">
            {(Boolean(search?.trim()) ||
              Boolean(filterDate?.startDate && filterDate?.endDate) ||
              Boolean(
                Array.isArray(selectedAgent) && selectedAgent.length > 0,
              ) ||
              Boolean(selectedReminderType) ||
              Boolean(selectedStatus)) && (
              <Button
                onClick={() => {
                  onClearFilter();
                  setSearch("");
                  // setSelectedAgent(null);
                  // setSelectedReminderType("");
                  // setSelectedReminderTypeValue("");
                  // setSelectedStatus("");
                  // setSelectedStatusLabel("");
                  setFilterDate({ startDate: null, endDate: null });
                  // 🔹 Trigger full clear with empty values
                  // setSelectedStatus(undefined);

                  // 🔹 Notify parent filter state (if handler provided)
                  try {
                    const api = (calendarRef.current as any)?.getApi?.();
                    if (api && typeof api.today === "function") {
                      api.today(); // navigate calendar to current month
                    }
                  } catch (e) {
                    // no-op
                  }
                }}
                size="sm"
                className=" border-slate-400 py-2.5"
                variant="outline-secondary"
              >
                <Lucide
                  icon="FilterX"
                  className="text-slate-600 mr-2 w-4 h-4"
                />
                Clear
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Calendar */}
      <Card className="p-6">
        <div className="full-calendar reminder-calendar">
          <FullCalendar ref={calendarRef as any} {...options} />
        </div>
      </Card>

      {/* Legend */}
      <Card className="mt-10 p-4 border border-slate-200 ">
        <div className="flex flex-row gap-x-2 justify-end">
          <p>Legend</p>
          {remindersColorsTips?.map((item: any, index: number) => (
            <Tippy
              as="div"
              options={{ theme: "light" }}
              content={item?.name}
              key={index}
              className="flex items-center"
            >
              <div className={`w-4 h-4 rounded-full ${item?.code}`} />
            </Tippy>
          ))}
        </div>
      </Card>

      {/* Reminder Action Modal - Only show when no parent callback is provided */}
      {!onEventClick && (
        <Dialog
          open={showActionModal}
          onClose={handleCloseModal}
          className="w-full max-w-md"
        >
          <Dialog.Panel>
            <Dialog.Title>
              <h2 className="mr-auto text-base font-medium">
                {selectedReminder?.status === "completed" ||
                selectedReminder?.status === "snoozed"
                  ? "Reminder Details"
                  : "Reminder Actions"}
              </h2>
            </Dialog.Title>
            <Dialog.Description>
              {selectedReminder && (
                <div className="space-y-4">
                  <div>
                    <h3 className="font-medium text-slate-900">
                      {selectedReminder.title}
                    </h3>
                    <p className="text-sm text-slate-600 mt-1">
                      {selectedReminder.description}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                      <span>
                        Due:{" "}
                        {moment(selectedReminder.start).format(
                          "MMM DD, YYYY HH:mm",
                        )}
                      </span>
                      <span>Assigned to: {selectedReminder.assignedTo}</span>
                      {selectedReminder.reminderType && (
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            selectedReminder.reminderType === TYPE_CALL
                              ? "bg-blue-100 text-blue-700"
                              : selectedReminder.reminderType === TYPE_FOLLOW_UP
                                ? "bg-green-100 text-green-700"
                                : selectedReminder.reminderType === TYPE_EMAIL
                                  ? "bg-red-100 text-red-700"
                                  : selectedReminder.reminderType ===
                                      TYPE_BOOKING
                                    ? "bg-purple-100 text-purple-700"
                                    : selectedReminder.reminderType ===
                                        TYPE_PAYMENT
                                      ? "bg-amber-100 text-amber-700"
                                      : selectedReminder.reminderType ===
                                          TYPE_DOCUMENTATION
                                        ? "bg-teal-100 text-teal-700"
                                        : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {selectedReminder.reminderData?.reminder_type_text ||
                            selectedReminder.reminderType
                              .replace("_", " ")
                              .toUpperCase()}
                        </span>
                      )}
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          selectedReminder.status === "completed"
                            ? "bg-green-100 text-green-700"
                            : selectedReminder.status === "overdue"
                              ? "bg-red-100 text-red-700"
                              : selectedReminder.status === "snoozed"
                                ? "bg-blue-100 text-blue-700"
                                : "bg-orange-100 text-orange-700"
                        }`}
                      >
                        {selectedReminder.status?.toUpperCase()}
                      </span>
                    </div>
                  </div>

                  {selectedReminder.status === "completed" ||
                  selectedReminder.status === "snoozed" ? (
                    <div className="space-y-4">
                      <div
                        className={`border rounded-lg p-4 ${
                          selectedReminder.status === "completed"
                            ? "bg-green-50"
                            : "bg-blue-50"
                        }`}
                      >
                        <div className="flex items-center mb-3">
                          <Lucide
                            icon={
                              selectedReminder.status === "completed"
                                ? "CheckCircle"
                                : "Clock"
                            }
                            className={`w-5 h-5 mr-2 ${
                              selectedReminder.status === "completed"
                                ? "text-green-600"
                                : "text-blue-600"
                            }`}
                          />
                          <h4
                            className={`font-medium ${
                              selectedReminder.status === "completed"
                                ? "text-green-900"
                                : "text-blue-900"
                            }`}
                          >
                            {selectedReminder.status === "completed"
                              ? "Reminder Completed"
                              : "Reminder Snoozed"}
                          </h4>
                        </div>

                        <div className="space-y-3 text-sm">
                          {selectedReminder.reminderData && (
                            <>
                              <div>
                                <span className="font-medium text-slate-700">
                                  Reminder ID:
                                </span>
                                <span className="ml-2 text-slate-600">
                                  #{selectedReminder.reminderData.id}
                                </span>
                              </div>

                              <div>
                                <span className="font-medium text-slate-700">
                                  Created:
                                </span>
                                <span className="ml-2 text-slate-600">
                                  {moment(
                                    selectedReminder.reminderData.created_at,
                                  ).format("MMM DD, YYYY HH:mm")}
                                </span>
                              </div>

                              <div>
                                <span className="font-medium text-slate-700">
                                  Last Updated:
                                </span>
                                <span className="ml-2 text-slate-600">
                                  {moment(
                                    selectedReminder.reminderData.updated_at,
                                  ).format("MMM DD, YYYY HH:mm")}
                                </span>
                              </div>

                              {selectedReminder.reminderData
                                .remind_before_minutes && (
                                <div>
                                  <span className="font-medium text-slate-700">
                                    Reminder Before:
                                  </span>
                                  <span className="ml-2 text-slate-600">
                                    {
                                      selectedReminder.reminderData
                                        .remind_before_minutes
                                    }{" "}
                                    minutes
                                  </span>
                                </div>
                              )}

                              {selectedReminder.reminderData.created_by && (
                                <div>
                                  <span className="font-medium text-slate-700">
                                    Created By:
                                  </span>
                                  <span className="ml-2 text-slate-600">
                                    {
                                      selectedReminder.reminderData.created_by
                                        .name
                                    }{" "}
                                    {
                                      selectedReminder.reminderData.created_by
                                        .last_name
                                    }
                                    {selectedReminder.reminderData.created_by
                                      .email && (
                                      <span className="text-slate-500">
                                        {" "}
                                        (
                                        {
                                          selectedReminder.reminderData
                                            .created_by.email
                                        }
                                        )
                                      </span>
                                    )}
                                  </span>
                                </div>
                              )}

                              {/* Show completion details for completed/snoozed reminders */}
                              {(selectedReminder.status === "completed" ||
                                selectedReminder.status === "snoozed") &&
                                selectedReminder.assignments &&
                                selectedReminder.assignments.length > 0 && (
                                  <div className="space-y-3">
                                    <h5 className="font-medium text-slate-700">
                                      Assignment Details:
                                    </h5>
                                    {selectedReminder.assignments.map(
                                      (assignment, index) => (
                                        <div
                                          key={assignment.id}
                                          className="border-l-4 border-slate-200 pl-3 py-2"
                                        >
                                          <div className="flex items-center justify-between mb-1">
                                            <span className="text-sm font-medium text-slate-600">
                                              Assignment #{index + 1}
                                            </span>
                                            <span
                                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                assignment.status ===
                                                "completed"
                                                  ? "bg-green-100 text-green-700"
                                                  : assignment.status ===
                                                      "overdue"
                                                    ? "bg-red-100 text-red-700"
                                                    : assignment.status ===
                                                        "snoozed"
                                                      ? "bg-orange-100 text-orange-700"
                                                      : "bg-gray-100 text-gray-700"
                                              }`}
                                            >
                                              {assignment.status_text}
                                            </span>
                                          </div>

                                          {assignment.completed_at && (
                                            <div className="text-sm text-slate-600 mb-1">
                                              <span className="font-medium">
                                                Completed:
                                              </span>{" "}
                                              {moment(
                                                assignment.completed_at,
                                              ).format("MMM DD, YYYY HH:mm")}
                                            </div>
                                          )}

                                          {assignment.completion_remarks && (
                                            <div className="text-sm">
                                              <span className="font-medium text-slate-600">
                                                Remarks:
                                              </span>
                                              <div className="mt-1 p-2 bg-slate-50 rounded text-slate-600 text-xs">
                                                {assignment.completion_remarks}
                                              </div>
                                            </div>
                                          )}

                                          {assignment.snoozed_until && (
                                            <div className="text-sm text-slate-600">
                                              <span className="font-medium">
                                                Snoozed Until:
                                              </span>{" "}
                                              {moment(
                                                assignment.snoozed_until,
                                              ).format("MMM DD, YYYY HH:mm")}
                                              {assignment.snooze_count > 0 && (
                                                <span className="ml-2 text-orange-600">
                                                  (Snoozed{" "}
                                                  {assignment.snooze_count} time
                                                  {assignment.snooze_count > 1
                                                    ? "s"
                                                    : ""}
                                                  )
                                                </span>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      ),
                                    )}
                                  </div>
                                )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Active Reminder Actions */
                    <>
                      {/* Complete Reminder Section */}
                      <div className="border rounded-lg p-4">
                        <h4 className="font-medium text-slate-900 mb-3">
                          Mark as Complete
                        </h4>
                        <FormTextarea
                          value={completionRemarks}
                          onChange={(e) => setCompletionRemarks(e.target.value)}
                          placeholder="Add completion remarks..."
                          rows={3}
                          className="w-full"
                        />
                        <Button
                          onClick={handleCompleteReminder}
                          disabled={actionLoading || !completionRemarks.trim()}
                          className="mt-3 w-full bg-green-600 hover:bg-green-700"
                        >
                          {actionLoading ? (
                            <div className="flex items-center justify-center">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Completing...
                            </div>
                          ) : (
                            <>
                              <Lucide
                                icon="CheckCircle"
                                className="w-4 h-4 mr-2"
                              />
                              Complete Reminder
                            </>
                          )}
                        </Button>
                      </div>

                      {/* Snooze Reminder Section */}
                      <div className="border rounded-lg p-4">
                        <h4 className="font-medium text-slate-900 mb-3">
                          Snooze Reminder
                        </h4>
                        <FormSelect
                          value={snoozeMinutes}
                          onChange={(e) => setSnoozeMinutes(e.target.value)}
                          className="w-full mb-3"
                        >
                          {snoozeOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </FormSelect>
                        <Button
                          onClick={handleSnoozeReminder}
                          disabled={actionLoading}
                          variant="outline-secondary"
                          className="w-full"
                        >
                          {actionLoading ? (
                            <div className="flex items-center justify-center">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-600 mr-2"></div>
                              Snoozing...
                            </div>
                          ) : (
                            <>
                              <Lucide icon="Clock" className="w-4 h-4 mr-2" />
                              Snooze for{" "}
                              {
                                snoozeOptions.find(
                                  (opt) => opt.value === snoozeMinutes,
                                )?.label
                              }
                            </>
                          )}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </Dialog.Description>
            <Dialog.Footer>
              <Button
                type="button"
                variant="outline-secondary"
                onClick={handleCloseModal}
                className="w-20 mr-1"
              >
                Cancel
              </Button>
            </Dialog.Footer>
          </Dialog.Panel>
        </Dialog>
      )}
    </div>
  );
}
// const mapStateToProps = ({ users, authUser }: RootState) => ({
//   users: users?.data,
//   authUser: authUser,
// });

// const mapDispatchToProps = (dispatch: any) => ({
//   fetchUsers: (payload: any) => dispatch(fetchAllUsers(payload)),
// });

// export default connect(mapStateToProps, mapDispatchToProps)(ReminderCalendar);
export default ReminderCalendar;
